/**
 * Canvas 基底類別
 *
 * 提供所有 Canvas 元件的共用功能：
 * - Canvas 初始化和縮放
 * - 滑鼠/觸控事件處理
 * - 座標轉換
 * - 渲染循環
 */

import { CANVAS } from '@/config/constants';
import type { Point } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

/** Canvas 設定選項 */
export interface CanvasOptions {
  /** Canvas 元素或 ID */
  canvas: HTMLCanvasElement | string;

  /** 最大高度（像素） */
  maxHeight?: number;

  /** 是否啟用高 DPI 支援 */
  enableHighDpi?: boolean;

  /** 游標樣式 */
  cursor?: string;
}

/** 事件處理器類型 */
export type CanvasEventHandler = (point: Point, event: PointerEvent) => void;

// ========================================================================
// BaseCanvas 類別
// ========================================================================

export abstract class BaseCanvas {
  /** Canvas 元素 */
  protected canvas: HTMLCanvasElement;

  /** Canvas 2D 繪圖上下文 */
  protected ctx: CanvasRenderingContext2D;

  /** 背景圖片 */
  protected image: HTMLImageElement | null = null;

  /** 基礎縮放比例（canvas 尺寸 / 圖片尺寸） */
  protected baseScale: number = 1;

  /** 縮放比例（考慮用戶縮放） */
  protected scale: number = 1;

  /** 用戶縮放倍率 */
  protected userScale: number = 1;

  /** 平移偏移量 X（圖片座標系） */
  protected panX: number = 0;

  /** 平移偏移量 Y（圖片座標系） */
  protected panY: number = 0;

  /** 圖片偏移量 X（已棄用，保留向後相容） */
  protected offsetX: number = 0;

  /** 圖片偏移量 Y（已棄用，保留向後相容） */
  protected offsetY: number = 0;

  /** 最大高度 */
  protected maxHeight: number;

  /** 最小縮放倍率 */
  protected minUserScale: number = 0.5;

  /** 最大縮放倍率 */
  protected maxUserScale: number = 5;

  /** 是否啟用高 DPI */
  protected enableHighDpi: boolean;

  /** 設備像素比 */
  protected dpr: number = 1;

  /** 事件移除函式列表 */
  private eventCleanups: Array<() => void> = [];

  /** 是否已銷毀 */
  private destroyed: boolean = false;

  constructor(options: CanvasOptions) {
    // 取得 Canvas 元素
    if (typeof options.canvas === 'string') {
      const element = document.getElementById(options.canvas);
      if (!(element instanceof HTMLCanvasElement)) {
        throw new Error(`Element "${options.canvas}" is not a canvas`);
      }
      this.canvas = element;
    } else {
      this.canvas = options.canvas;
    }

    // 取得繪圖上下文
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;

    // 設定選項
    this.maxHeight = options.maxHeight ?? CANVAS.MAX_HEIGHT;
    this.enableHighDpi = options.enableHighDpi ?? true;
    this.dpr = this.enableHighDpi ? (window.devicePixelRatio || 1) : 1;

    // 設定游標
    if (options.cursor) {
      this.canvas.style.cursor = options.cursor;
    }
  }

  // ========================================================================
  // 初始化方法
  // ========================================================================

  /**
   * 設定背景圖片並調整 Canvas 尺寸
   */
  setImage(image: HTMLImageElement): void {
    this.image = image;
    this.setupCanvasSize();
    this.render();
  }

  /**
   * 根據圖片調整 Canvas 尺寸
   */
  protected setupCanvasSize(): void {
    if (!this.image) return;

    const parent = this.canvas.parentElement;
    const maxWidth = parent?.clientWidth ?? this.canvas.width;

    const imgAspect = this.image.width / this.image.height;
    // 永遠使用容器寬度，高度根據圖片比例計算
    const canvasWidth = maxWidth;
    const canvasHeight = maxWidth / imgAspect;

    // 設定 Canvas 顯示尺寸（永遠填滿容器寬度）
    this.canvas.style.width = `${canvasWidth}px`;
    this.canvas.style.height = `${canvasHeight}px`;

    // 設定 Canvas 實際尺寸（考慮高 DPI）
    this.canvas.width = canvasWidth * this.dpr;
    this.canvas.height = canvasHeight * this.dpr;

    // 計算基礎縮放比例
    this.baseScale = canvasWidth / this.image.width;
    this.scale = this.baseScale * this.userScale;
    this.offsetX = 0;
    this.offsetY = 0;

    // 如果啟用高 DPI，調整上下文縮放
    if (this.enableHighDpi && this.dpr !== 1) {
      this.ctx.scale(this.dpr, this.dpr);
    }
  }

  // ========================================================================
  // 座標轉換
  // ========================================================================

  /**
   * 取得滑鼠/觸控在圖片座標系統的位置（考慮縮放和平移）
   */
  protected getImagePoint(event: PointerEvent | MouseEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    const canvasX = event.clientX - rect.left;
    const canvasY = event.clientY - rect.top;
    return {
      x: canvasX / this.scale + this.panX,
      y: canvasY / this.scale + this.panY,
    };
  }

  /**
   * 取得滑鼠/觸控在 Canvas 座標系統的位置
   */
  protected getCanvasPoint(event: PointerEvent | MouseEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  /**
   * 將圖片座標轉換為 Canvas 座標（考慮縮放和平移）
   */
  protected imageToCanvas(point: Point): Point {
    return {
      x: (point.x - this.panX) * this.scale,
      y: (point.y - this.panY) * this.scale,
    };
  }

  /**
   * 將 Canvas 座標轉換為圖片座標（考慮縮放和平移）
   */
  protected canvasToImage(point: Point): Point {
    return {
      x: point.x / this.scale + this.panX,
      y: point.y / this.scale + this.panY,
    };
  }

  // ========================================================================
  // 事件處理
  // ========================================================================

  /**
   * 繫結 Pointer 事件
   */
  protected bindPointerEvents(handlers: {
    onPointerDown?: CanvasEventHandler;
    onPointerMove?: CanvasEventHandler;
    onPointerUp?: CanvasEventHandler;
    onDoubleClick?: CanvasEventHandler;
  }): void {
    const { onPointerDown, onPointerMove, onPointerUp, onDoubleClick } = handlers;

    if (onPointerDown) {
      const handler = (e: PointerEvent) => {
        e.preventDefault();
        onPointerDown(this.getImagePoint(e), e);
      };
      this.canvas.addEventListener('pointerdown', handler);
      this.eventCleanups.push(() => this.canvas.removeEventListener('pointerdown', handler));
    }

    if (onPointerMove) {
      const handler = (e: PointerEvent) => {
        e.preventDefault();
        onPointerMove(this.getImagePoint(e), e);
      };
      this.canvas.addEventListener('pointermove', handler);
      this.eventCleanups.push(() => this.canvas.removeEventListener('pointermove', handler));
    }

    if (onPointerUp) {
      const handler = (e: PointerEvent) => {
        e.preventDefault();
        onPointerUp(this.getImagePoint(e), e);
      };
      this.canvas.addEventListener('pointerup', handler);
      this.eventCleanups.push(() => this.canvas.removeEventListener('pointerup', handler));
    }

    if (onDoubleClick) {
      const handler = (e: MouseEvent) => {
        e.preventDefault();
        onDoubleClick(this.getImagePoint(e as unknown as PointerEvent), e as unknown as PointerEvent);
      };
      this.canvas.addEventListener('dblclick', handler);
      this.eventCleanups.push(() => this.canvas.removeEventListener('dblclick', handler));
    }
  }

  // ========================================================================
  // 繪圖輔助方法
  // ========================================================================

  /**
   * 清空 Canvas
   */
  protected clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);
  }

  /**
   * 繪製背景圖片（考慮縮放和平移）
   */
  protected drawImage(): void {
    if (!this.image) return;

    this.ctx.save();
    this.ctx.scale(this.scale, this.scale);
    this.ctx.translate(-this.panX, -this.panY);
    this.ctx.drawImage(this.image, 0, 0);
    this.ctx.restore();
  }

  /**
   * 開始使用圖片座標系統繪製（考慮縮放和平移）
   * 在此之後的繪製座標都是圖片座標
   */
  protected beginImageCoords(): void {
    this.ctx.save();
    this.ctx.scale(this.scale, this.scale);
    this.ctx.translate(-this.panX, -this.panY);
  }

  /**
   * 結束圖片座標系統繪製
   */
  protected endImageCoords(): void {
    this.ctx.restore();
  }

  // ========================================================================
  // 抽象方法（子類別必須實作）
  // ========================================================================

  /**
   * 渲染 Canvas 內容
   */
  abstract render(): void;

  /**
   * 初始化並繫結事件
   */
  abstract initialize(): void;

  // ========================================================================
  // 縮放和平移
  // ========================================================================

  /**
   * 以指定點為中心進行縮放
   * @param delta 縮放增量（正數放大，負數縮小）
   * @param centerX Canvas 座標 X（縮放中心）
   * @param centerY Canvas 座標 Y（縮放中心）
   */
  zoom(delta: number, centerX?: number, centerY?: number): void {
    const oldScale = this.userScale;
    const newScale = Math.max(
      this.minUserScale,
      Math.min(this.maxUserScale, oldScale + delta)
    );

    if (newScale === oldScale) return;

    // 如果提供了中心點，以該點為中心縮放
    if (centerX !== undefined && centerY !== undefined) {
      // 計算縮放前的圖片座標
      const imageX = centerX / this.scale + this.panX;
      const imageY = centerY / this.scale + this.panY;

      // 更新縮放
      this.userScale = newScale;
      this.scale = this.baseScale * this.userScale;

      // 調整平移使縮放中心保持不變
      this.panX = imageX - centerX / this.scale;
      this.panY = imageY - centerY / this.scale;
    } else {
      // 以 Canvas 中心為縮放中心
      const canvasWidth = this.canvas.width / this.dpr;
      const canvasHeight = this.canvas.height / this.dpr;
      this.zoom(delta, canvasWidth / 2, canvasHeight / 2);
      return;
    }

    // 限制平移範圍
    this.constrainPan();
    this.render();
  }

  /**
   * 設定縮放倍率
   */
  setZoom(scale: number): void {
    const clampedScale = Math.max(
      this.minUserScale,
      Math.min(this.maxUserScale, scale)
    );
    if (clampedScale === this.userScale) return;

    this.userScale = clampedScale;
    this.scale = this.baseScale * this.userScale;
    this.constrainPan();
    this.render();
  }

  /**
   * 重設縮放和平移
   */
  resetZoom(): void {
    this.userScale = 1;
    this.scale = this.baseScale;
    this.panX = 0;
    this.panY = 0;
    this.render();
  }

  /**
   * 取得當前用戶縮放倍率
   */
  getUserScale(): number {
    return this.userScale;
  }

  /**
   * 限制平移範圍，確保圖片不會完全移出視圖
   */
  protected constrainPan(): void {
    if (!this.image) return;

    const canvasWidth = this.canvas.width / this.dpr;
    const canvasHeight = this.canvas.height / this.dpr;
    const visibleWidth = canvasWidth / this.scale;
    const visibleHeight = canvasHeight / this.scale;

    // 允許圖片邊緣到達 Canvas 中心
    const maxPanX = Math.max(0, this.image.width - visibleWidth / 2);
    const maxPanY = Math.max(0, this.image.height - visibleHeight / 2);
    const minPanX = Math.min(0, -visibleWidth / 2);
    const minPanY = Math.min(0, -visibleHeight / 2);

    this.panX = Math.max(minPanX, Math.min(maxPanX, this.panX));
    this.panY = Math.max(minPanY, Math.min(maxPanY, this.panY));
  }

  /**
   * 處理滾輪縮放事件
   */
  protected handleWheel(event: WheelEvent): void {
    event.preventDefault();

    const rect = this.canvas.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;

    // 計算縮放增量
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    this.zoom(delta, centerX, centerY);
  }

  /**
   * 啟用滾輪縮放
   */
  protected enableWheelZoom(): void {
    const handler = (e: WheelEvent) => this.handleWheel(e);
    this.canvas.addEventListener('wheel', handler, { passive: false });
    this.eventCleanups.push(() => this.canvas.removeEventListener('wheel', handler));
  }

  // ========================================================================
  // 生命週期
  // ========================================================================

  /**
   * 銷毀 Canvas 實例，清理所有事件監聯器
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    // 移除所有事件監聽器
    for (const cleanup of this.eventCleanups) {
      cleanup();
    }
    this.eventCleanups = [];

    // 清空 Canvas
    this.clear();
  }

  /**
   * 取得 Canvas 元素
   */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /**
   * 取得當前縮放比例
   */
  getScale(): number {
    return this.scale;
  }

  /**
   * 取得圖片尺寸
   */
  getImageSize(): { width: number; height: number } | null {
    if (!this.image) return null;
    return {
      width: this.image.width,
      height: this.image.height,
    };
  }
}
